import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Select from 'react-select';

const typeOptions = [
  { value: 'ROLES', label: 'Roles' },
  { value: 'BP_NUMBERS', label: 'BP Numbers' },
];

import {
  AddButtonContainer,
  AssignmentTypeSelectContainer,
  CenteredH2,
  DocumentInput,
  GeneralDocumentPickerContainer,
  ModalActions,
  ModalContent,
  ModalField,
  ModalLoadingContent,
  ModalOverlay,
  RemoveDocumentButton,
  RemoveDocumentButtonContainer,
  ScrollableContent,
  SubHeadlineContainer,
  SubHeadlineSection,
} from './styles';
import ActionButton from '../../../components/ActionButton/ActionButton';
import {
  cropConnectApiClient,
  GeneralDocumentDto,
  WidgetDto,
  WidgetType,
} from '../../../../services/cropConnectApiClient';
import { useI18n } from '../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import GeneralDocumentPicker from '../../../components/GeneralDocumentPicker/GeneralDocumentPicker';

interface Option {
  value: string;
  label: string;
}

const GeneralDocumentListCreateModal = ({
  isOpen,
  onClose,
  widget,
  parent,
  newOrder,
  onCreateSuccess,
  onUpdateSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  widget: WidgetDto | null;
  parent: string;
  newOrder: number;
  onCreateSuccess: (widget: WidgetDto) => void;
  onUpdateSuccess: (widget: WidgetDto) => void;
}) => {
  const { t } = useI18n();
  const isEditing = !!widget; // If widget exists, we're editing
  const [loading, setLoading] = useState(false);
  const [generalDocuments, setGeneralDocuments] =
    useState<GeneralDocumentDto[]>();
  const [headline, setHeadline] = useState({ id: uuidv4(), text: '' });
  const [directDocumentInputFields, setDirectDocumentInputFields] = useState([
    {
      id: '',
      documentDisplayName: '',
      generalDocumentEcmId: null,
      parentFolder: null,
      generalDocumentEcmName: null,
      assignmentType: { value: 'ROLES', label: 'Roles' },
      selectedRoles: [] as Option[],
      bpNumbers: '',
    },
  ]);
  const [roles, setRoles] = useState<Option[]>([]);
  const [documentInputFields, setDocumentInputFields] = useState([
    {
      subHeadline: { id: uuidv4(), text: '' },
      documents: [
        {
          id: uuidv4(),
          documentDisplayName: '',
          generalDocumentEcmId: null,
          parentFolder: null,
          generalDocumentEcmName: null,
          assignmentType: { value: 'ROLES', label: 'Roles' },
          selectedRoles: [] as Option[],
          bpNumbers: '',
        },
      ],
    },
  ]);

  const resetState = () => {
    setHeadline({ id: uuidv4(), text: '' });
    setDirectDocumentInputFields([
      {
        id: '',
        documentDisplayName: '',
        generalDocumentEcmId: null,
        parentFolder: null,
        generalDocumentEcmName: null,
        assignmentType: { value: 'ROLES', label: 'Roles' },
        selectedRoles: [],
        bpNumbers: '',
      },
    ]);
    setDocumentInputFields([
      {
        subHeadline: { id: uuidv4(), text: '' },
        documents: [
          {
            id: uuidv4(),
            documentDisplayName: '',
            generalDocumentEcmId: null,
            parentFolder: null,
            generalDocumentEcmName: null,
            assignmentType: { value: 'ROLES', label: 'Roles' },
            selectedRoles: [],
            bpNumbers: '',
          },
        ],
      },
    ]);
  };
  const handleSave = async () => {
    setLoading(true); // Show loading indicator
    try {
      if (isEditing) {
        widget.propertyBag?.headlines.filter((h) => h.type === 'SUB_HEADLINE');
        const originalSubHeadlineIds = widget.propertyBag?.headlines
          .filter((h) => h.type === 'SUB_HEADLINE')
          .map((h) => h.id);
        const documentInputFieldIds = documentInputFields.map(
          (input) => input.subHeadline.id
        );
        const deletedSubHeadlineIds = originalSubHeadlineIds.filter(
          (id) => !documentInputFieldIds.includes(id)
        );

        const subheadlinesThatHaveDocuments = generalDocuments?.map(
          (doc) => doc.parentHeadline
        );

        if (
          deletedSubHeadlineIds.some((item) =>
            subheadlinesThatHaveDocuments?.includes(item)
          )
        ) {
          alert(t(TranslationKeys.general_document_delete_subheadline_warning));
          setLoading(false);
          return;
        }
        const headlineFromStore = widget.propertyBag?.headlines.find(
          (h) => h.type === 'HEADLINE'
        );
        const updatedWidget = await cropConnectApiClient.updateWidget(
          parent,
          widget.accessId!,
          {
            widgetType: WidgetType.GENERAL_DOCUMENT_LIST,
            order: widget.order,
            propertyBag: {
              headlines: [
                {
                  id: headlineFromStore?.id || uuidv4(),
                  text: headline.text,
                  type: 'HEADLINE',
                },
                ...documentInputFields.map((input) => ({
                  id: input.subHeadline.id,
                  text: input.subHeadline.text,
                  type: 'SUB_HEADLINE',
                })),
              ],
            },
          }
        );
        if (updatedWidget.success) {
          onUpdateSuccess(updatedWidget.data!);
        } else {
          setLoading(false); // Hide loading indicator
          return;
        }

        const generalDocumentsIds = generalDocuments?.map(
          (doc) => doc.accessId
        );
        for (let i = 0; i < directDocumentInputFields.length; i++) {
          const document = directDocumentInputFields[i];
          if (generalDocumentsIds?.includes(document.id)) {
            await cropConnectApiClient.updateGeneralDocument(
              widget.accessId!,
              document.id,
              {
                parentFolderId: document.parentFolder,
                documentId: document.generalDocumentEcmId,
                visibleFor:
                  document.assignmentType.value === 'ROLES'
                    ? document.selectedRoles.map((role) => role.value)
                    : document.bpNumbers.split('\n'),
                properties: {
                  name: document.generalDocumentEcmName,
                  assignmentType: document.assignmentType.value,
                  generalDocumentEcmName: document.generalDocumentEcmName,
                  parentFolderId: document.parentFolder,
                  documentDisplayName:
                    document.documentDisplayName ||
                    document.generalDocumentEcmName,
                },
              }
            );
          } else {
            if (document.generalDocumentEcmId === null) {
              continue;
            }
            await cropConnectApiClient.addGeneralDocument(widget.accessId!, {
              visibleFor:
                document.assignmentType.value === 'ROLES'
                  ? document.selectedRoles.map((role) => role.value)
                  : document.bpNumbers.split('\n'),
              properties: {
                name: document.generalDocumentEcmName,
                assignmentType: document.assignmentType.value,
                generalDocumentEcmName: document.generalDocumentEcmName,
                parentFolderId: document.parentFolder,
                documentDisplayName:
                  document.documentDisplayName ||
                  document.generalDocumentEcmName,
              },
              parentHeadline: widget.propertyBag?.headlines.find(
                (h) => h.type === 'HEADLINE'
              )?.id,
              parentFolderId: document.parentFolder,
              documentId: document.generalDocumentEcmId,
            });
          }
        }

        for (let i = 0; i < documentInputFields.length; i++) {
          const documentInputField = documentInputFields[i];
          for (let j = 0; j < documentInputField.documents.length; j++) {
            const document = documentInputField.documents[j];
            if (generalDocumentsIds?.includes(document.id)) {
              await cropConnectApiClient.updateGeneralDocument(
                widget.accessId!,
                document.id,
                {
                  parentFolderId: document.parentFolder,
                  documentId: document.generalDocumentEcmId,
                  visibleFor:
                    document.assignmentType.value === 'ROLES'
                      ? document.selectedRoles.map((role) => role.value)
                      : document.bpNumbers.split('\n'),
                  properties: {
                    name: document.generalDocumentEcmName,
                    assignmentType: document.assignmentType.value,
                    generalDocumentEcmName: document.generalDocumentEcmName,
                    parentFolderId: document.parentFolder,
                    documentDisplayName:
                      document.documentDisplayName ||
                      document.generalDocumentEcmName,
                  },
                }
              );
            } else {
              if (document.generalDocumentEcmId === null) {
                continue;
              }
              await cropConnectApiClient.addGeneralDocument(widget.accessId!, {
                visibleFor:
                  document.assignmentType.value === 'ROLES'
                    ? document.selectedRoles.map((role) => role.value)
                    : document.bpNumbers.split('\n'),
                properties: {
                  name: document.generalDocumentEcmName,
                  assignmentType: document.assignmentType.value,
                  generalDocumentEcmName: document.generalDocumentEcmName,
                  parentFolderId: document.parentFolder,
                  documentDisplayName:
                    document.documentDisplayName ||
                    document.generalDocumentEcmName,
                },
                parentHeadline: documentInputFields[i].subHeadline.id,
                parentFolderId: document.parentFolder,
                documentId: document.generalDocumentEcmId,
              });
            }
          }
        }

        const documentIds = documentInputFields
          .map((input) => input.documents.map((doc) => doc.id))
          .flat();
        const directDocumentIds = directDocumentInputFields.map(
          (doc) => doc.id
        );
        const allDocumentIds = [...documentIds, ...directDocumentIds];
        if (!generalDocuments) return;
        for (let i = 0; i < generalDocuments.length; i++) {
          const doc = generalDocuments[i];
          if (allDocumentIds.indexOf(doc.accessId!) === -1) {
            await cropConnectApiClient.deleteGeneralDocument(
              widget.accessId!,
              doc.accessId!
            );
          }
        }
      } else {
        const createdWidget = await cropConnectApiClient.addWidget(parent, {
          widgetType: WidgetType.GENERAL_DOCUMENT_LIST,
          order: newOrder,
          propertyBag: {
            headlines: [
              { id: headline.id, text: headline.text, type: 'HEADLINE' },
              ...documentInputFields.map((input) => ({
                id: input.subHeadline.id,
                text: input.subHeadline.text,
                type: 'SUB_HEADLINE',
              })),
            ],
          },
        });
        if (!createdWidget.success) {
          setLoading(false); // Hide loading indicator
          return;
        }
        onCreateSuccess(createdWidget.data!);
        //Documents under the headlines
        for (let i = 0; i < directDocumentInputFields.length; i++) {
          const document = directDocumentInputFields[i];
          if (document.generalDocumentEcmId === null) {
            continue;
          }
          await cropConnectApiClient.addGeneralDocument(
            createdWidget.data?.accessId ?? '',
            {
              visibleFor:
                document.assignmentType.value === 'ROLES'
                  ? document.selectedRoles.map((role) => role.value)
                  : document.bpNumbers.split('\n'),
              properties: {
                name: document.generalDocumentEcmName,
                assignmentType: document.assignmentType.value,
                generalDocumentEcmName: document.generalDocumentEcmName,
                parentFolderId: document.parentFolder,
                documentDisplayName:
                  document.documentDisplayName ||
                  document.generalDocumentEcmName,
              },
              parentHeadline: createdWidget.data?.propertyBag?.headlines.find(
                (h) => h.type === 'HEADLINE'
              )?.id,
              parentFolderId: document.parentFolder,
              documentId: document.generalDocumentEcmId,
            }
          );
        }
        //Documents under the subheadlines
        for (let i = 0; i < documentInputFields.length; i++) {
          const documentInputField = documentInputFields[i];
          for (let j = 0; j < documentInputField.documents.length; j++) {
            const document = documentInputField.documents[j];
            if (document.generalDocumentEcmId === null) {
              continue;
            }
            await cropConnectApiClient.addGeneralDocument(
              createdWidget.data?.accessId ?? '',
              {
                visibleFor:
                  document.assignmentType.value === 'ROLES'
                    ? document.selectedRoles.map((role) => role.value)
                    : document.bpNumbers.split('\n'),
                properties: {
                  name: document.generalDocumentEcmName,
                  assignmentType: document.assignmentType.value,
                  generalDocumentEcmName: document.generalDocumentEcmName,
                  parentFolderId: document.parentFolder,
                  documentDisplayName:
                    document.documentDisplayName ||
                    document.generalDocumentEcmName,
                },

                parentHeadline: documentInputFields[i].subHeadline.id,
                parentFolderId: document.parentFolder,
                documentId: document.generalDocumentEcmId,
              }
            );
          }
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      resetState();
      onClose();
      window.location.reload();
    }
  };

  const handleOnCancel = () => {
    resetState();
    onClose();
  };

  const handleDirectDocumentInputDisplayNameChange = (index: number, event) => {
    const data = [...directDocumentInputFields];
    data[index].documentDisplayName = event.target.value;
    setDirectDocumentInputFields(data);
  };

  const handleDirectDocumentInputChange = (
    index: number,
    generalDocumentEcmId,
    parentFolderId,
    generalDocumentEcmName
  ) => {
    const data = [...directDocumentInputFields];
    data[index]['generalDocumentEcmId'] = generalDocumentEcmId;
    data[index]['parentFolder'] = parentFolderId;
    data[index]['generalDocumentEcmName'] = generalDocumentEcmName;
    setDirectDocumentInputFields(data);
  };

  const handleDirectDocumentInputTypeChange = (index, selected) => {
    const data = [...directDocumentInputFields];
    data[index].assignmentType = selected;
    setDirectDocumentInputFields(data);
  };

  const handleDirectDocumentInputRolesChange = (index, selected) => {
    const data = [...directDocumentInputFields];
    data[index].selectedRoles = selected;
    setDirectDocumentInputFields(data);
  };

  const handleDirectDocumentInputBpNumberChange = (index, event) => {
    const data = [...directDocumentInputFields];
    data[index].bpNumbers = event.target.value;
    setDirectDocumentInputFields(data);
  };

  const addDirectDocumentInputField = () => {
    const newfield = {
      id: uuidv4(),
      documentDisplayName: '',
      generalDocumentEcmId: null,
      parentFolder: null,
      generalDocumentEcmName: null,
      assignmentType: { value: 'ROLES', label: 'Roles' },
      selectedRoles: [] as Option[],
      bpNumbers: '',
    };

    setDirectDocumentInputFields([...directDocumentInputFields, newfield]);
  };

  const handleSubheadlineChange = (index: number, event) => {
    const data = [...documentInputFields];
    data[index].subHeadline.text = event.target.value;
    setDocumentInputFields(data);
  };

  const handleDocumentInputDisplayNameChange = (index1, index2, event) => {
    const data = [...documentInputFields];
    data[index1].documents[index2].documentDisplayName = event.target.value;
    setDocumentInputFields(data);
  };

  const handleFormChange = (
    index1: number,
    index2: number,
    generalDocumentEcmId,
    parentFolderId,
    generalDocumentEcmName
  ) => {
    const data = [...documentInputFields];
    data[index1].documents[index2]['generalDocumentEcmId'] =
      generalDocumentEcmId;
    data[index1].documents[index2]['parentFolder'] = parentFolderId;
    data[index1].documents[index2]['generalDocumentEcmName'] =
      generalDocumentEcmName;

    setDocumentInputFields(data);
  };

  const handleTypeChange = (index1, index2, selected) => {
    const data = [...documentInputFields];
    data[index1].documents[index2].assignmentType = selected;
    setDocumentInputFields(data);
  };

  const handleRolesChange = (index1, index2, selected) => {
    const data = [...documentInputFields];
    data[index1].documents[index2].selectedRoles = selected;
    setDocumentInputFields(data);
  };

  const handleBpNumberChange = (index1, index2, event) => {
    const data = [...documentInputFields];
    data[index1].documents[index2].bpNumbers = event.target.value;
    setDocumentInputFields(data);
  };

  const addSubHeadlineSection = () => {
    const newSection = {
      subHeadline: { id: uuidv4(), text: '' },
      documents: [
        {
          id: uuidv4(),
          documentDisplayName: '',
          generalDocumentEcmId: null,
          parentFolder: null,
          generalDocumentEcmName: null,
          assignmentType: { value: 'ROLES', label: 'Roles' },
          selectedRoles: [],
          bpNumbers: '',
        },
      ],
    };

    setDocumentInputFields([...documentInputFields, newSection]);
  };

  const addDocumentInput = (index) => {
    const newDocumentInputFields = [...documentInputFields];
    newDocumentInputFields[index].documents.push({
      id: uuidv4(),
      documentDisplayName: '',
      generalDocumentEcmId: null,
      parentFolder: null,
      generalDocumentEcmName: null,
      assignmentType: { value: 'ROLES', label: 'Roles' },
      selectedRoles: [],
      bpNumbers: '',
    });

    setDocumentInputFields(newDocumentInputFields);
  };

  const removeDirectDocumentInputField = (index) => {
    const newDocumentInputFields = [...directDocumentInputFields];
    newDocumentInputFields.splice(index, 1);
    setDirectDocumentInputFields(newDocumentInputFields);
  };

  const removeSubHeadlineSection = (index) => {
    const newDocumentInputFields = [...documentInputFields];
    newDocumentInputFields.splice(index, 1);
    setDocumentInputFields(newDocumentInputFields);
  };

  const removeDocumentInput = (index1, index2) => {
    const newDocumentInputFields = [...documentInputFields];
    newDocumentInputFields[index1].documents.splice(index2, 1);
    setDocumentInputFields(newDocumentInputFields);
  };

  useEffect(() => {
    const fetchGeneralDocuments = async () => {
      if (widget?.accessId === undefined) return;
      const generalDocuments =
        await cropConnectApiClient.getAllGeneralDocumentsForParent(
          widget.accessId
        );
      const directDocumentInputFields = generalDocuments.data
        ?.filter(
          (doc) =>
            doc.parentHeadline ===
            widget.propertyBag?.headlines.find((h) => h.type === 'HEADLINE').id
        )
        .map((doc) => ({
          id: doc.accessId,
          documentDisplayName: doc.propertyBag?.documentDisplayName,
          generalDocumentEcmId: doc.ecmDocumentId,
          parentFolder: doc.propertyBag?.parentFolderId,
          generalDocumentEcmName: doc.propertyBag?.generalDocumentEcmName,
          assignmentType: {
            value: doc.propertyBag?.assignmentType,
            label:
              doc.propertyBag?.assignmentType === 'ROLES'
                ? 'Roles'
                : 'BP Numbers',
          },
          selectedRoles:
            doc.propertyBag?.assignmentType === 'ROLES'
              ? doc.visibleFor?.map((role) => ({
                  value: role,
                  label: role,
                }))
              : [],
          bpNumbers:
            doc.propertyBag?.assignmentType === 'BP_NUMBERS'
              ? doc.visibleFor?.join('\n')
              : '',
        }));
      generalDocuments.data?.forEach((doc) =>
        console.log(doc.parentHeadline, headline.id)
      );

      setDirectDocumentInputFields(directDocumentInputFields);

      const documentInputFields = widget.propertyBag?.headlines
        .filter((h) => h.type === 'SUB_HEADLINE')
        .map((h) => ({
          subHeadline: { id: h.id, text: h.text },
          documents: generalDocuments.data
            ?.filter((doc) => doc.parentHeadline === h.id)
            ?.map((doc) => ({
              id: doc.accessId,
              documentDisplayName: doc.propertyBag?.documentDisplayName,
              generalDocumentEcmId: doc.ecmDocumentId,
              parentFolder: doc.propertyBag?.parentFolderId,
              generalDocumentEcmName: doc.propertyBag?.generalDocumentEcmName,
              assignmentType: {
                value: doc.propertyBag?.assignmentType,
                label:
                  doc.propertyBag?.assignmentType === 'ROLES'
                    ? 'Roles'
                    : 'BP Numbers',
              },
              selectedRoles:
                doc.propertyBag?.assignmentType === 'ROLES'
                  ? doc.visibleFor?.map((role) => ({
                      value: role,
                      label: role,
                    }))
                  : [],
              bpNumbers:
                doc.propertyBag?.assignmentType === 'BP_NUMBERS'
                  ? doc.visibleFor?.join('\n')
                  : '',
            })),
        }));
      setDocumentInputFields(documentInputFields);
    };
    if (isEditing) {
      const headlineFromStore = widget.propertyBag?.headlines.find(
        (h) => h.type === 'HEADLINE'
      );
      setHeadline({ id: headlineFromStore?.id, text: headlineFromStore?.text });

      fetchGeneralDocuments();
    } else {
      // setHeadline('');
      // setSubheadline('');
    }
  }, [widget, isEditing]);

  useEffect(() => {
    const fetchRoles = async () => {
      const roles =
        await cropConnectApiClient.getGeneralDocumentsApplicableRoles();
      if (roles.data) {
        setRoles(roles.data.map((role) => ({ value: role, label: role })));
      }
    };
    if (isOpen) {
      fetchRoles();
    }
    const getGeneralDocuments = async () => {
      if (widget?.accessId === undefined) return;
      const generalDocuments =
        await cropConnectApiClient.getAllGeneralDocumentsForParent(
          widget.accessId
        );
      setGeneralDocuments(generalDocuments.data);
    };
    getGeneralDocuments();
  }, [widget, isOpen]);

  if (!isOpen) return null;

  if (loading) {
    return (
      <ModalOverlay>
        <ModalLoadingContent>
          <CenteredH2>{t(TranslationKeys.general_document_loading)}</CenteredH2>
        </ModalLoadingContent>
      </ModalOverlay>
    );
  }
  return (
    <ModalOverlay>
      <ModalContent>
        <CenteredH2>
          {t(TranslationKeys.create_general_document_list_title)}
        </CenteredH2>
        <ScrollableContent>
          <ModalField>
            <label>
              {t(TranslationKeys.create_general_document_banner_headline)}
            </label>
            <input
              type="text"
              value={headline.text}
              onChange={(e) =>
                setHeadline({ ...headline, text: e.target.value })
              }
            />
          </ModalField>

          {directDocumentInputFields.map((input, index) => {
            return (
              <DocumentInput key={index}>
                <ModalField>
                  <label>
                    {t(TranslationKeys.create_general_document_document_name)}
                  </label>
                  <input
                    name="documentName"
                    type="text"
                    value={input.documentDisplayName}
                    onChange={(event) =>
                      handleDirectDocumentInputDisplayNameChange(index, event)
                    }
                  />
                </ModalField>
                <GeneralDocumentPickerContainer>
                  <div>
                    {input.generalDocumentEcmName
                      ? input.generalDocumentEcmName
                      : t(
                          TranslationKeys.create_general_document_modal_choose_document
                        )}
                  </div>
                  <GeneralDocumentPicker
                    onGeneralDocumentPicked={(
                      generalDocumentEcmId,
                      parentFolderId,
                      generalDocumentEcmName
                    ) => {
                      handleDirectDocumentInputChange(
                        index,
                        generalDocumentEcmId,
                        parentFolderId,
                        generalDocumentEcmName
                      );
                    }}
                  />
                </GeneralDocumentPickerContainer>
                <AssignmentTypeSelectContainer>
                  <Select
                    options={typeOptions}
                    value={input.assignmentType}
                    onChange={(selected) =>
                      handleDirectDocumentInputTypeChange(index, selected)
                    }
                    classNamePrefix="select"
                    placeholder="Select an option"
                  />
                </AssignmentTypeSelectContainer>
                {input?.assignmentType.value === 'ROLES' && (
                  <ModalField>
                    <Select
                      options={roles}
                      isMulti
                      value={input.selectedRoles}
                      onChange={(selected) =>
                        handleDirectDocumentInputRolesChange(index, selected)
                      }
                      className="basic-multi-select"
                      classNamePrefix="select"
                    />
                  </ModalField>
                )}
                {input?.assignmentType.value === 'BP_NUMBERS' && (
                  <ModalField>
                    <textarea
                      value={input.bpNumbers}
                      onChange={(event) =>
                        handleDirectDocumentInputBpNumberChange(index, event)
                      }
                      placeholder="Enter BP Numbers (one BP number per line)"
                    />
                  </ModalField>
                )}
                <RemoveDocumentButtonContainer>
                  <RemoveDocumentButton>
                    <ActionButton
                      variant="abort"
                      buttonText={t(
                        TranslationKeys.general_document_remove_document_button
                      )}
                      onClickAction={() =>
                        removeDirectDocumentInputField(index)
                      }
                    />
                  </RemoveDocumentButton>
                </RemoveDocumentButtonContainer>
              </DocumentInput>
            );
          })}
          <AddButtonContainer>
            <ActionButton
              variant="accept"
              buttonText={t(
                TranslationKeys.general_document_add_document_button
              )}
              onClickAction={addDirectDocumentInputField}
            />
          </AddButtonContainer>
          {documentInputFields.map((input, index1) => {
            return (
              <SubHeadlineSection>
                <SubHeadlineContainer>
                  <ModalField>
                    <label>
                      {t(
                        TranslationKeys.create_general_document_list_subheadline
                      )}
                    </label>
                    <input
                      name="subHeadline"
                      type="text"
                      value={input.subHeadline.text}
                      onChange={(event) =>
                        handleSubheadlineChange(index1, event)
                      }
                    />
                  </ModalField>
                  <RemoveDocumentButtonContainer>
                    <RemoveDocumentButton>
                      <ActionButton
                        variant="abort"
                        buttonText={t(
                          TranslationKeys.general_document_remove_subheadline_button
                        )}
                        onClickAction={() => removeSubHeadlineSection(index1)}
                      />
                    </RemoveDocumentButton>
                  </RemoveDocumentButtonContainer>
                </SubHeadlineContainer>
                {input.documents.map((documentInput, index2) => {
                  return (
                    <DocumentInput>
                      <ModalField>
                        <label>
                          {t(
                            TranslationKeys.create_general_document_document_name
                          )}
                        </label>
                        <input
                          name="documentName"
                          type="text"
                          value={documentInput.documentDisplayName}
                          onChange={(event) =>
                            handleDocumentInputDisplayNameChange(
                              index1,
                              index2,
                              event
                            )
                          }
                        />
                      </ModalField>
                      <GeneralDocumentPickerContainer>
                        <div>
                          {documentInput.generalDocumentEcmName
                            ? documentInput.generalDocumentEcmName
                            : t(
                                TranslationKeys.create_general_document_modal_choose_document
                              )}
                        </div>
                        <GeneralDocumentPicker
                          onGeneralDocumentPicked={(
                            generalDocumentEcmId,
                            parentFolderId,
                            generalDocumentEcmName
                          ) => {
                            handleFormChange(
                              index1,
                              index2,
                              generalDocumentEcmId,
                              parentFolderId,
                              generalDocumentEcmName
                            );
                          }}
                        />
                      </GeneralDocumentPickerContainer>
                      <AssignmentTypeSelectContainer>
                        <Select
                          options={typeOptions}
                          value={documentInput.assignmentType}
                          onChange={(selected) =>
                            handleTypeChange(index1, index2, selected)
                          }
                          classNamePrefix="select"
                          placeholder="Select an option"
                        />
                      </AssignmentTypeSelectContainer>
                      {documentInput?.assignmentType.value === 'ROLES' && (
                        <ModalField>
                          <Select
                            options={roles}
                            isMulti
                            value={documentInput.selectedRoles}
                            onChange={(selected) =>
                              handleRolesChange(index1, index2, selected)
                            }
                            className="basic-multi-select"
                            classNamePrefix="select"
                          />
                        </ModalField>
                      )}
                      {documentInput?.assignmentType.value === 'BP_NUMBERS' && (
                        <ModalField>
                          <textarea
                            value={documentInput.bpNumbers}
                            onChange={(event) =>
                              handleBpNumberChange(index1, index2, event)
                            }
                            placeholder="Enter BP Numbers (one BP number per line)"
                          />
                        </ModalField>
                      )}
                      <RemoveDocumentButtonContainer>
                        <RemoveDocumentButton>
                          <ActionButton
                            variant="abort"
                            buttonText={t(
                              TranslationKeys.general_document_remove_document_button
                            )}
                            onClickAction={() =>
                              removeDocumentInput(index1, index2)
                            }
                          />
                        </RemoveDocumentButton>
                      </RemoveDocumentButtonContainer>
                    </DocumentInput>
                  );
                })}
                <AddButtonContainer>
                  <ActionButton
                    variant="accept"
                    buttonText={t(
                      TranslationKeys.general_document_add_document_button
                    )}
                    onClickAction={() => addDocumentInput(index1)}
                  />
                </AddButtonContainer>
              </SubHeadlineSection>
            );
          })}
          <AddButtonContainer>
            <ActionButton
              variant="accept"
              buttonText={t(
                TranslationKeys.general_document_add_subheadline_button
              )}
              onClickAction={addSubHeadlineSection}
            />
          </AddButtonContainer>
        </ScrollableContent>
        <ModalActions>
          <ActionButton
            variant="close"
            buttonText={t(TranslationKeys.general_document_cancel_button)}
            onClickAction={handleOnCancel}
          />
          <ActionButton
            variant="accept-blue"
            buttonText={
              isEditing
                ? t(TranslationKeys.general_document_save_button)
                : t(TranslationKeys.general_document_create_button)
            }
            onClickAction={handleSave}
          />
        </ModalActions>
      </ModalContent>
    </ModalOverlay>
  );
};

export default GeneralDocumentListCreateModal;
